import React, { useRef, useState } from 'react'

const formatFileSize = (bytes) => {
  if (!bytes) return '0 Bytes'
  const units = ['Bytes', 'KB', 'MB', 'GB']
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  const value = bytes / (1024 ** index)
  return `${Math.round(value * 100) / 100} ${units[index]}`
}

const toAttachmentRecords = (fileList) => {
  const files = Array.from(fileList || [])
  return files
    .filter((file) => String(file.type || '').startsWith('image/'))
    .map((file, index) => ({
      id: `img-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 8)}`,
      name: file.name,
      size: file.size,
      file
    }))
}

export default function PhotoAttachmentPicker({
  label = 'Upload Attachments',
  files = [],
  onAddFiles,
  onRemoveFile,
  disabled = false,
  multiple = true,
  allowFileUploads = false
}) {
  const [dragActive, setDragActive] = useState(false)
  const [showSourceOptions, setShowSourceOptions] = useState(false)
  const photoInputRef = useRef(null)
  const cameraInputRef = useRef(null)
  const fileInputRef = useRef(null)

  const addFiles = (fileList) => {
    const sourceFiles = Array.from(fileList || [])
    const next = allowFileUploads
      ? sourceFiles.map((file, index) => ({
        id: `att-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 8)}`,
        name: file.name,
        size: file.size,
        file
      }))
      : toAttachmentRecords(sourceFiles)

    if (next.length > 0) {
      onAddFiles?.(next)
    }
  }

  const handleDrag = (event) => {
    event.preventDefault()
    event.stopPropagation()
    if (event.type === 'dragenter' || event.type === 'dragover') {
      setDragActive(true)
    } else if (event.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (event) => {
    event.preventDefault()
    event.stopPropagation()
    setDragActive(false)
    if (disabled) return
    addFiles(event.dataTransfer?.files)
  }

  const handlePhotoInputChange = (event) => {
    addFiles(event.target.files)
    event.target.value = ''
    setShowSourceOptions(false)
  }

  const handleCameraInputChange = (event) => {
    addFiles(event.target.files)
    event.target.value = ''
    setShowSourceOptions(false)
  }

  const handleFileInputChange = (event) => {
    addFiles(event.target.files)
    event.target.value = ''
    setShowSourceOptions(false)
  }

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>

      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => !disabled && setShowSourceOptions(true)}
        className={`border-2 border-dashed rounded-lg p-6 text-center transition ${
          dragActive
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-300 hover:border-blue-500'
        } ${disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
      >
        <div className="mb-2 flex justify-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            className="h-10 w-10 text-blue-600"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M21.44 11.05 12.25 20.24a6 6 0 1 1-8.49-8.49l8.49-8.49a4 4 0 1 1 5.66 5.66L9.4 17.43a2 2 0 1 1-2.83-2.83l7.78-7.78" />
          </svg>
        </div>
        <p className="text-sm text-gray-600">Tap to choose upload source</p>
        <p className="text-xs text-gray-400 mt-2">{allowFileUploads ? 'Camera, Photo, or Files' : 'Camera or Photo only'}</p>
      </div>

      {showSourceOptions && !disabled && (
        <div
          className="fixed inset-0 z-[70] bg-black/40 flex items-end sm:items-center justify-center p-4"
          onClick={() => setShowSourceOptions(false)}
        >
          <div
            className="w-full max-w-sm bg-white rounded-t-2xl sm:rounded-2xl shadow-xl p-4 sm:p-5"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 className="text-base font-semibold text-gray-900">Select Upload Source</h3>
            <p className="text-sm text-gray-500 mt-1">Choose how you want to attach files.</p>
            <div className="mt-4 grid grid-cols-1 gap-2">
              <button
                type="button"
                onClick={() => cameraInputRef.current?.click()}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-blue-50 text-blue-700 text-sm font-semibold hover:bg-blue-100"
              >
                Camera
              </button>
              <button
                type="button"
                onClick={() => photoInputRef.current?.click()}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-gray-700 text-sm font-semibold hover:bg-gray-50"
              >
                Photo
              </button>
              {allowFileUploads && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-gray-700 text-sm font-semibold hover:bg-gray-50"
                >
                  Files
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowSourceOptions(false)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-gray-500 text-sm font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <input
        ref={photoInputRef}
        type="file"
        accept="image/*"
        multiple={multiple}
        onChange={handlePhotoInputChange}
        className="hidden"
        disabled={disabled}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleCameraInputChange}
        className="hidden"
        disabled={disabled}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.zip"
        multiple={multiple}
        onChange={handleFileInputChange}
        className="hidden"
        disabled={disabled}
      />

      {files.length > 0 && (
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 mt-3">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-semibold text-gray-700 text-sm">Selected Files ({files.length})</h3>
            <button
              type="button"
              onClick={() => !disabled && setShowSourceOptions(true)}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Add More
            </button>
          </div>
          <div className="max-h-64 overflow-y-auto space-y-2 scrollbar-hidden">
            {files.map((fileObj) => (
              <div
                key={fileObj.id}
                className="flex items-center justify-between bg-white p-3 rounded border border-gray-200 hover:bg-gray-50"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className="inline-flex items-center justify-center min-w-10 h-6 px-2 rounded bg-blue-100 text-blue-700 text-xs font-semibold">
                    {String(fileObj?.file?.type || '').startsWith('image/') ? 'IMG' : 'FILE'}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-700 truncate font-medium">{fileObj.name}</p>
                    <p className="text-xs text-gray-500">{formatFileSize(fileObj.size)}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onRemoveFile?.(fileObj.id)}
                  className="text-red-600 hover:text-red-800 text-lg ml-2 flex-shrink-0"
                  title="Remove file"
                >
                  x
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
